import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, ExternalLink } from "lucide-react";

interface Maker {
  id: string;
  name: string;
  handle: string;
  ageDays: number;
  stripe: "done" | "started" | "none";
  openedComposer: boolean;
  hasListing: boolean;
  lastSeen: string | null;
}
interface MakersResponse {
  funnel: { storefronts: number; paymentsReady: number; listing: number };
  makers: Maker[];
}

// One-line diagnosis of where a maker without a live listing got stuck. This is
// the whole point of the drill-down: turn "N didn't list" into a to-do list.
function stallReason(m: Maker): string {
  if (m.hasListing) return "Listing ✓";
  if (!m.openedComposer) return "Never opened the composer";
  if (m.stripe !== "done") return "Opened composer, no payments set up";
  return "Set up + opened composer, never published";
}

function relDay(iso: string | null): string {
  if (!iso) return "no app activity";
  const d = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (d <= 0) return "seen today";
  return `seen ${d}d ago`;
}

export default function AdminMakers() {
  const { data, isLoading } = useQuery<MakersResponse>({
    queryKey: ["/api/admin/makers"],
    queryFn: async () => {
      const res = await fetch("/api/admin/makers", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const ghosts = data?.makers.filter((m) => !m.hasListing) ?? [];
  const listing = data?.makers.filter((m) => m.hasListing) ?? [];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-5">
          <Button variant="ghost" size="sm" asChild className="mb-3">
            <Link href="/admin">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to dashboard
            </Link>
          </Button>
          <h1 className="mb-1 text-xl font-bold">Makers</h1>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">Couldn't load makers.</p>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <div className="text-2xl font-bold">{data.funnel.storefronts}</div>
                  <div className="text-xs text-muted-foreground">Storefronts</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-2xl font-bold">{data.funnel.paymentsReady}</div>
                  <div className="text-xs text-muted-foreground">Payments-ready</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-2xl font-bold">{data.funnel.listing}</div>
                  <div className="text-xs text-muted-foreground">Actively listing</div>
                </div>
              </div>

              <section>
                <h2 className="mb-2 text-sm font-semibold">
                  Stalled — storefront, no live listing ({ghosts.length})
                </h2>
                <div className="space-y-2">
                  {ghosts.map((m) => (
                    <Card key={m.id}>
                      <CardContent className="flex items-center justify-between gap-3 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium">{m.name}</span>
                            <a
                              href={`/buy/${m.handle}`}
                              target="_blank"
                              rel="noreferrer"
                              className="shrink-0 text-muted-foreground hover:text-foreground"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stallReason(m)} · {m.ageDays}d old · {relDay(m.lastSeen)}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                            m.stripe === "done"
                              ? "bg-brand-secondary/15 text-brand-secondary"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {m.stripe === "done" ? "Stripe ✓" : m.stripe === "started" ? "Stripe started" : "No Stripe"}
                        </span>
                      </CardContent>
                    </Card>
                  ))}
                  {ghosts.length === 0 && (
                    <p className="text-sm text-muted-foreground">No stalled makers — everyone with a shop is listing.</p>
                  )}
                </div>
              </section>

              <section>
                <h2 className="mb-2 text-sm font-semibold">Listing ({listing.length})</h2>
                <div className="space-y-2">
                  {listing.map((m) => (
                    <Card key={m.id}>
                      <CardContent className="flex items-center justify-between gap-3 py-3">
                        <span className="truncate font-medium">{m.name}</span>
                        <a
                          href={`/buy/${m.handle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
                        >
                          /buy/{m.handle}
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
